import { useState, useEffect, useMemo } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import BusinessLogicFields from './BusinessLogicFields'
import { type DesignerCatalog } from './catalog'
import { modalOf } from './KitModal'
import {
  type Validation, type ValidationRule, type ValidationType,
  type ValidationSeverity,
  type BuiltinKind, type ValueRef, type ValueSource, type MasterCondition,
  type RestApiParam,
  VALIDATION_TYPE_LABELS, BUILTIN_KIND_LABELS, VALIDATION_SEVERITY_LABELS,
  isValidationRule, legacyStringToRule, validationSummary,
} from '../txn/types'

interface FormRef { form_no: string; title: string }

interface ValidationRuleEditorProps {
  open: boolean
  onClose: () => void
  value: Validation
  onSave: (value: Validation) => void
  /** Columns of the current form — offered as {form.X} tokens / pickers. */
  formColumnNames: string[]
  /** Columns of the parent/header form — offered as {header.X} tokens. */
  headerColumnNames?: string[]
  /** All forms in the transaction (for "child form must have an entry"). */
  formList?: FormRef[]
  /** Project folder — required to fetch master tables/columns. */
  project: string
  title?: string
  /** Deep-link a business_logic function to the code editor. */
  onOpenCode?: (method: string) => void
  /** General-Process only: result-grid column names. When provided, the editor
      shows a "Result column this rule checks" selector bound to `rule.column`
      (the transaction designer attaches a rule to a field, so it omits this). */
  valueColumnNames?: string[]
  /** Where master tables, columns, project variables and IOFlow APIs come
   *  from, and how a message is shown (designerKit/catalog). Absent ⇒ free
   *  text pickers. */
  catalog?: DesignerCatalog
  /** What the rule checks. `'criteria'` = a visual / dashboard / report
   *  criterion (criteria_validation): checked when the object runs, so the
   *  record-shaped built-ins (unique, child form) and "Runs on" do not apply,
   *  and an error blocks the RUN. Default `'record'`. */
  context?: 'record' | 'criteria'
}

// Built-ins that describe a record — nothing a criterion value can be checked
// for (the engine drops them on a criterion too).
const RECORD_ONLY_BUILTINS: BuiltinKind[] = ['must_be_unique', 'child_form_has_entry']
const CRITERIA_SEVERITY_LABELS: Record<ValidationSeverity, string> = {
  E: 'Error — stops the run until it is fixed',
  W: 'Warning — shown beside the criteria, the run goes ahead',
  P: 'Prompt — information only, the run goes ahead',
}

interface TableSummary { name: string; description?: string }
interface ColumnSummary { column_name: string; data_type?: string }

function cmpNoCase(a: string, b: string) {
  return a.toLowerCase().localeCompare(b.toLowerCase())
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '7px 10px', background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)', borderRadius: 6,
  color: 'var(--color-text)', fontSize: '0.9125rem', minHeight: 36, boxSizing: 'border-box',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
  fontWeight: 600,
  marginBottom: 4, marginTop: 12,
}
const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.8625rem', fontWeight: 600, color: 'var(--color-accent)',
  marginTop: 14, marginBottom: 6, paddingBottom: 4,
  borderBottom: '1px solid var(--color-border)',
}
const helpStyle: React.CSSProperties = {
  fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
  marginTop: 4, fontStyle: 'italic',
}
const addBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
  padding: '4px 8px', fontSize: '0.8125rem', color: 'var(--color-accent)',
  background: 'none', border: '1px dashed var(--color-border)', borderRadius: 4,
  cursor: 'pointer', width: '100%', marginTop: 6,
}
const rowStyle: React.CSSProperties = {
  display: 'flex', gap: 6, alignItems: 'center', marginTop: 6,
}

interface InsertOption { label: string; token: string }

// Date / math / string / logic helpers offered in expression pickers. The
// inserted token seeds a sensible argument the author edits (e.g. years_ago(18)).
// These mirror the engine's _BOUND_FUNCS whitelist (expr_eval.SAFE_FUNCS +
// executor._DATE_FUNCS) — keep in sync with patterns/validations.md.
const CALENDAR_FN_OPTIONS: InsertOption[] = [
  { label: 'age(dob)', token: 'age({form.dob})' },
  { label: 'today()', token: 'today()' },
  { label: 'now()', token: 'now()' },
  { label: 'date(year, month, day)', token: 'date(2025, 1, 1)' },
  { label: 'days_ago(n)', token: 'days_ago(30)' },
  { label: 'days_from_now(n)', token: 'days_from_now(30)' },
  { label: 'months_ago(n)', token: 'months_ago(6)' },
  { label: 'months_from_now(n)', token: 'months_from_now(1)' },
  { label: 'years_ago(n)', token: 'years_ago(18)' },
  { label: 'years_from_now(n)', token: 'years_from_now(1)' },
  { label: 'date_add(d, n, unit)', token: 'date_add({form.dt}, 1, "month")' },
  { label: 'date_sub(d, n, unit)', token: 'date_sub({form.dt}, 1, "year")' },
  { label: 'date_diff(d1, d2, unit)', token: 'date_diff({form.end}, {form.start}, "day")' },
  { label: 'min(a, b)', token: 'min(' },
  { label: 'max(a, b)', token: 'max(' },
  { label: 'round(x, n)', token: 'round(' },
  { label: 'abs(x)', token: 'abs(' },
  { label: 'iif(cond, a, b)', token: 'iif(' },
  { label: 'coalesce(…)', token: 'coalesce(' },
  { label: 'is_empty(x) / not_empty(x)', token: 'not_empty(' },
  { label: 'in_list(x, "A,B,C")', token: 'in_list(' },
  { label: 'contains(s, sub)', token: 'contains(' },
  { label: 'len / lower / upper / trim', token: 'lower(' },
]

// A compact "insert a token" dropdown — always resets to its placeholder so it
// can be used repeatedly. Used for fields, calendar functions and variables.
function InsertMenu({
  label, options, onPick, style,
}: {
  label: string
  options: InsertOption[]
  onPick: (token: string) => void
  style?: React.CSSProperties
}) {
  if (!options.length) return null
  return (
    <select value="" style={{ ...inputStyle, cursor: 'pointer', ...style }}
      onChange={(e) => { if (e.target.value) onPick(e.target.value) }}>
      <option value="">{label}</option>
      {options.map((o, i) => (
        <option key={i} value={o.token}>{o.label}</option>
      ))}
    </select>
  )
}

function freshRule(): ValidationRule {
  return {
    type: 'builtin', name: '', message: '',
    builtin: { kind: 'must_exist_in_master', conditions: [] },
  }
}

// ── small controls ─────────────────────────────────────────────────────────

function ValueRefControl({
  value, onChange, formColumnNames, headerColumnNames, projectVars = [],
  allowExpr = false,
}: {
  value?: ValueRef
  onChange: (v: ValueRef) => void
  formColumnNames: string[]
  headerColumnNames: string[]
  /** Project-variable names — offered as {var.NAME} tokens in expressions. */
  projectVars?: string[]
  /** Offer "Expression" as a source (range bounds / date checks). */
  allowExpr?: boolean
}) {
  const ref: ValueRef = value || { source: 'const', value: '' }
  const cols = ref.source === 'form' ? formColumnNames : headerColumnNames
  const appendToExpr = (t: string) => onChange({ ...ref, value: (ref.value || '') + t })
  const fieldOptions: InsertOption[] = [
    ...formColumnNames.map((c) => ({ label: `{form.${c}}`, token: `{form.${c}}` })),
    ...headerColumnNames.map((c) => ({ label: `{header.${c}}`, token: `{header.${c}}` })),
  ]
  const varOptions: InsertOption[] = projectVars.map((v) => ({ label: `{var.${v}}`, token: `{var.${v}}` }))
  const pickerStyle: React.CSSProperties = { fontSize: '0.8125rem', padding: '4px 6px' }
  return (
    <div style={{ display: 'flex', gap: 6, flex: 1 }}>
      <select
        value={ref.source}
        onChange={(e) => onChange({ source: e.target.value as ValueSource, value: '' })}
        style={{ ...inputStyle, maxWidth: 130, cursor: 'pointer' }}
      >
        <option value="const">Constant</option>
        <option value="form">Form column</option>
        <option value="header">Header column</option>
        {allowExpr && <option value="expr">Expression</option>}
      </select>
      {ref.source === 'const' ? (
        <input style={inputStyle} value={ref.value}
          onChange={(e) => onChange({ ...ref, value: e.target.value })}
          placeholder="value" />
      ) : ref.source === 'expr' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
          <input style={{ ...inputStyle, fontFamily: 'monospace' }} value={ref.value}
            onChange={(e) => onChange({ ...ref, value: e.target.value })}
            placeholder="e.g. years_ago(20) or today() - (20 * 365)" />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <InsertMenu label="＋ Field…" options={fieldOptions} onPick={appendToExpr} style={pickerStyle} />
            <InsertMenu label="ƒ Function…" options={CALENDAR_FN_OPTIONS} onPick={appendToExpr} style={pickerStyle} />
            <InsertMenu label="＋ Variable…" options={varOptions} onPick={appendToExpr} style={pickerStyle} />
          </div>
        </div>
      ) : (
        <select style={{ ...inputStyle, cursor: 'pointer' }} value={ref.value}
          onChange={(e) => onChange({ ...ref, value: e.target.value })}>
          <option value="">— select column —</option>
          {cols.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
    </div>
  )
}

// Extra column-value conditions matched on the looked-up / duplicate row —
// shared by the must_exist_in_master and must_be_unique builtin editors.
function ConditionsEditor({
  title, help, conditions, onChange, columns, formColumnNames, headerColumnNames,
}: {
  title: string
  help?: string
  conditions: MasterCondition[]
  onChange: (next: MasterCondition[]) => void
  /** Target-table columns (datalist suggestions for the condition column). */
  columns: string[]
  formColumnNames: string[]
  headerColumnNames: string[]
}) {
  return (
    <>
      <div style={sectionTitleStyle}>{title}</div>
      {conditions.map((c, i) => (
        <div key={i} style={{ border: '1px solid var(--color-border)', borderRadius: 4, padding: 8, marginTop: 6 }}>
          <div style={rowStyle}>
            <ColumnInput value={c.column} columns={columns}
              onChange={(v) => {
                const next = [...conditions]; next[i] = { ...c, column: v }; onChange(next)
              }} placeholder="table column" />
            <select style={{ ...inputStyle, maxWidth: 70, cursor: 'pointer' }} value={c.op || '='}
              onChange={(e) => {
                const next = [...conditions]; next[i] = { ...c, op: e.target.value as MasterCondition['op'] }; onChange(next)
              }}>
              {['=', '!=', '>', '<', '>=', '<=', 'in'].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)' }}
              onClick={() => onChange(conditions.filter((_, j) => j !== i))}>
              <Trash2 size={14} />
            </button>
          </div>
          <div style={rowStyle}>
            <ValueRefControl value={{ source: c.source, value: c.value }}
              formColumnNames={formColumnNames} headerColumnNames={headerColumnNames}
              onChange={(vr) => {
                const next = [...conditions]; next[i] = { ...c, source: vr.source, value: vr.value }; onChange(next)
              }} />
          </div>
          <label style={{ ...helpStyle, display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!c.allow_null}
              onChange={(e) => {
                const next = [...conditions]; next[i] = { ...c, allow_null: e.target.checked }; onChange(next)
              }} />
            Also pass when the table column is NULL
          </label>
        </div>
      ))}
      <button style={addBtnStyle}
        onClick={() => onChange([...conditions, { column: '', op: '=', source: 'const', value: '' }])}>
        <Plus size={12} /> Add condition
      </button>
      {help && <p style={helpStyle}>{help}</p>}
    </>
  )
}

// A text input backed by an optional <datalist> of a master table's columns.
function ColumnInput({
  value, onChange, columns, placeholder,
}: {
  value: string
  onChange: (v: string) => void
  columns: string[]
  placeholder?: string
}) {
  const listId = useMemo(
    () => 'cols-' + Math.random().toString(36).slice(2), [],
  )
  return (
    <>
      <input style={inputStyle} value={value} list={columns.length ? listId : undefined}
        onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
      {columns.length > 0 && (
        <datalist id={listId}>
          {columns.map((c) => <option key={c} value={c} />)}
        </datalist>
      )}
    </>
  )
}

// ── editor ──────────────────────────────────────────────────────────────────

export default function ValidationRuleEditor({
  open, onClose, value, onSave, formColumnNames, headerColumnNames = [],
  formList = [], project, title: modalTitle, onOpenCode, valueColumnNames, catalog,
  context = 'record',
}: ValidationRuleEditorProps) {
  const forCriteria = context === 'criteria'
  const severityLabels = forCriteria ? CRITERIA_SEVERITY_LABELS : VALIDATION_SEVERITY_LABELS
  const Modal = modalOf(catalog)
  const notifyError = (msg: string) => {
    if (catalog) catalog.notify('error', msg)
    else console.error(`[ValidationRuleEditor] ${msg}`)
  }
  const [rule, setRule] = useState<ValidationRule>(freshRule())
  // Non-null when the incoming value is a legacy string we couldn't structure;
  // we preserve and re-emit it verbatim unless the user converts it.
  const [legacyRaw, setLegacyRaw] = useState<string | null>(null)

  const [tables, setTables] = useState<TableSummary[]>([])
  const [columnsByTable, setColumnsByTable] = useState<Record<string, ColumnSummary[]>>({})
  // Project variable names — offered as {var.NAME} tokens in expressions.
  const [projectVars, setProjectVars] = useState<string[]>([])

  useEffect(() => {
    if (!open) return
    if (isValidationRule(value)) {
      setRule(JSON.parse(JSON.stringify(value)))
      setLegacyRaw(null)
    } else if (typeof value === 'string' && value.trim()) {
      const parsed = legacyStringToRule(value)
      if (parsed) { setRule(parsed); setLegacyRaw(null) }
      else { setRule(freshRule()); setLegacyRaw(value) }
    } else {
      setRule(freshRule()); setLegacyRaw(null)
    }
  }, [open, value])

  // Active IOFlow APIs (Advanced → Events & APIs) for the Call API picker.
  const [ioflowApis, setIoflowApis] = useState<string[]>([])
  useEffect(() => {
    if (!open || !catalog?.integrationApis) return
    let cancelled = false
    catalog.integrationApis()
      .then((names) => { if (!cancelled) setIoflowApis(Array.isArray(names) ? names : []) })
      .catch(() => { /* no design yet */ })
    return () => { cancelled = true }
  }, [open, project, catalog])

  // Load master tables once the modal opens.
  useEffect(() => {
    if (!open || !catalog) return
    let cancelled = false
    catalog.tables()
      .then((res) => {
        if (cancelled) return
        const list: TableSummary[] = Array.isArray(res) ? res : []
        setTables([...list].sort((a, b) => cmpNoCase(a.name, b.name)))
      })
      .catch(() => { if (!cancelled) setTables([]) })
    return () => { cancelled = true }
  }, [open, project, catalog])

  // Load project variables (for {var.NAME} pickers) once the modal opens.
  useEffect(() => {
    if (!open || !catalog?.projectVariables) { setProjectVars([]); return }
    let cancelled = false
    catalog.projectVariables()
      .then((vars) => {
        if (cancelled) return
        setProjectVars((vars || []).map((v) => v.name).filter(Boolean).sort(cmpNoCase))
      })
      .catch(() => { if (!cancelled) setProjectVars([]) })
    return () => { cancelled = true }
  }, [open, project, catalog])

  const loadColumns = (table: string) => {
    const key = (table || '').trim().toLowerCase()
    if (!key || !catalog || columnsByTable[key]) return
    catalog.columns(table)
      .then((res) => {
        const cols: ColumnSummary[] = Array.isArray(res) ? res : []
        setColumnsByTable((prev) => ({ ...prev, [key]: cols }))
      })
      .catch(() => setColumnsByTable((prev) => ({ ...prev, [key]: [] })))
  }

  const colsOf = (table?: string): string[] => {
    const key = (table || '').trim().toLowerCase()
    return (columnsByTable[key] || []).map((c) => c.column_name)
  }

  const update = (partial: Partial<ValidationRule>) => setRule((r) => ({ ...r, ...partial }))
  const builtin = rule.builtin || { kind: 'must_exist_in_master' as BuiltinKind }
  const updateBuiltin = (partial: Partial<NonNullable<ValidationRule['builtin']>>) =>
    setRule((r) => ({ ...r, builtin: { ...(r.builtin || { kind: 'must_exist_in_master' }), ...partial } }))

  const changeType = (type: ValidationType) => {
    const base: ValidationRule = { ...rule, type }
    if (type === 'builtin' && !base.builtin) base.builtin = { kind: 'must_exist_in_master', conditions: [] }
    if (type === 'expression' && base.expression === undefined) base.expression = ''
    if (type === 'business_logic' && !base.business_logic) base.business_logic = { method: '', description: '' }
    if (type === 'rest_api' && !base.rest_api) base.rest_api = { url: '', method: 'POST', on_error: 'allow', params: [] }
    setRule(base)
  }

  const insertToken = (token: string) =>
    update({ message: `${rule.message || ''}${token}` })

  const handleSave = () => {
    if (legacyRaw !== null) { onSave(legacyRaw); onClose(); return }
    if (!(rule.name || '').trim()) { notifyError('Give the validation a name'); return }
    onSave(rule)
    onClose()
  }

  const openCode = () => {
    const method = (rule.business_logic?.method || '').trim()
    if (!method) { notifyError('Enter a function name first'); return }
    onSave(rule); onClose(); onOpenCode?.(method)
  }

  // Compact "insert a {form.X}/{header.X} token" picker for the message +
  // expression boxes — a dropdown instead of a wall of column chips.
  const InsertFieldSelect = ({ onPick }: { onPick: (t: string) => void }) => {
    if (!formColumnNames.length && !headerColumnNames.length) return null
    return (
      <select value="" style={{ ...inputStyle, cursor: 'pointer', marginTop: 6 }}
        onChange={(e) => { if (e.target.value) onPick(e.target.value) }}>
        <option value="">＋ Insert field…</option>
        {formColumnNames.length > 0 && (
          <optgroup label="Form columns">
            {formColumnNames.map((c) => (
              <option key={'f' + c} value={`{form.${c}}`}>{c}</option>
            ))}
          </optgroup>
        )}
        {headerColumnNames.length > 0 && (
          <optgroup label="Header columns">
            {headerColumnNames.map((c) => (
              <option key={'h' + c} value={`{header.${c}}`}>{c}</option>
            ))}
          </optgroup>
        )}
      </select>
    )
  }

  const cond = builtin.conditions || []
  const params = rule.rest_api?.params || []

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalTitle || 'Validation'}
      size="wide"
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave}>Save</button>
        </div>
      }
    >
      {legacyRaw !== null ? (
        <div>
          <label style={labelStyle}>Legacy validation (preserved as-is)</label>
          <textarea style={{ ...inputStyle, minHeight: 70, fontFamily: 'monospace' }}
            value={legacyRaw} onChange={(e) => setLegacyRaw(e.target.value)} />
          <button type="button" style={addBtnStyle}
            onClick={() => { setLegacyRaw(null); setRule(freshRule()) }}>
            Convert to a structured validation
          </button>
        </div>
      ) : (
      <div>
        {/* ── common header fields ── */}
        <label style={labelStyle}>Validation name</label>
        <input style={inputStyle} value={rule.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder="e.g. Customer must be active" />

        <label style={labelStyle}>Severity</label>
        <select style={{ ...inputStyle, cursor: 'pointer' }}
          value={rule.severity || 'E'}
          onChange={(e) => update({ severity: e.target.value as ValidationSeverity })}>
          {(Object.keys(severityLabels) as ValidationSeverity[]).map((s) => (
            <option key={s} value={s}>{severityLabels[s]}</option>
          ))}
        </select>
        {(rule.severity || 'E') === 'W' && (
          <p style={helpStyle}>
            A failing warning lets the user choose "Ignore warnings and proceed" —
            the save is retried and this rule is skipped. The error code below
            identifies the warning across that retry (recommended; falls back to
            the validation name).
          </p>
        )}
        {(rule.severity || 'E') === 'P' && (
          <p style={helpStyle}>
            A prompt only shows the message to the user — the save always
            proceeds, no confirmation needed.
          </p>
        )}

        <label style={labelStyle}>
          Error code (optional{(rule.severity || 'E') === 'W' ? ', the warning identity' : ', unique'})
        </label>
        <input style={inputStyle} value={rule.error_code || ''}
          onChange={(e) => update({ error_code: e.target.value })}
          placeholder="e.g. CUST_001" />

        <label style={{ ...labelStyle, display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!rule.disabled}
            onChange={(e) => update({ disabled: e.target.checked })} />
          Temporarily disable this validation ({forCriteria ? 'skipped when it runs' : 'skipped at save, logged as disabled'})
        </label>

        {!forCriteria && (
          <>
            <label style={labelStyle}>Runs on (optional)</label>
            <input style={inputStyle} value={(rule.run_on || []).join(', ')}
              onChange={(e) => {
                const ops = e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                update({ run_on: ops.length ? ops : undefined })
              }}
              placeholder="blank = every save · e.g. add, edit · or an action name such as confirm" />
            <p style={helpStyle}>
              A rule naming an action (confirm) is skipped on save and checked when that action button runs.
            </p>
          </>
        )}

        <label style={{ ...labelStyle, display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={!!rule.skip_if_blank}
            onChange={(e) => update({ skip_if_blank: e.target.checked || undefined })} />
          Skip this validation while the field is blank
        </label>

        <label style={labelStyle}>
          {(rule.severity || 'E') === 'E' ? 'Error message'
            : (rule.severity || 'E') === 'W' ? 'Warning message' : 'Prompt message'}
        </label>
        <textarea style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }}
          value={rule.message}
          onChange={(e) => update({ message: e.target.value })}
          placeholder="Shown when validation fails. Use {form.COL} / {header.COL} to insert values." />
        <InsertFieldSelect onPick={insertToken} />

        {/* ── type selector ── */}
        <label style={labelStyle}>Validation type</label>
        <select style={{ ...inputStyle, cursor: 'pointer' }} value={rule.type}
          onChange={(e) => changeType(e.target.value as ValidationType)}>
          {(Object.keys(VALIDATION_TYPE_LABELS) as ValidationType[]).map((t) => (
            <option key={t} value={t}>{VALIDATION_TYPE_LABELS[t]}</option>
          ))}
        </select>

        {/* ── result column (General Process only) ── */}
        {valueColumnNames && valueColumnNames.length > 0 && (
          <>
            <label style={labelStyle}>Result column this rule checks</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={rule.column || ''}
              onChange={(e) => update({ column: e.target.value || undefined })}>
              <option value="">— derive automatically —</option>
              {valueColumnNames.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <p style={helpStyle}>
              The result-grid column whose value is validated (for built-in checks)
              and the cell a failure highlights. Leave on "derive automatically" to
              infer it from the built-in's match column or the expression.
            </p>
          </>
        )}

        {/* ── BUILT-IN ── */}
        {rule.type === 'builtin' && (
          <>
            <label style={labelStyle}>Built-in check</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={builtin.kind}
              onChange={(e) => updateBuiltin({ kind: e.target.value as BuiltinKind })}>
              {(Object.keys(BUILTIN_KIND_LABELS) as BuiltinKind[])
                .filter((k) => !forCriteria || !RECORD_ONLY_BUILTINS.includes(k))
                .map((k) => (
                  <option key={k} value={k}>{BUILTIN_KIND_LABELS[k]}</option>
                ))}
            </select>

            {builtin.kind === 'must_exist_in_master' && (
              <>
                <label style={labelStyle}>Master table</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={builtin.table || ''}
                  onChange={(e) => { updateBuiltin({ table: e.target.value }); loadColumns(e.target.value) }}>
                  <option value="">— select table —</option>
                  {tables.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
                </select>
                <label style={labelStyle}>Match column (defaults to the lookup key)</label>
                <ColumnInput value={builtin.match_column || ''} columns={colsOf(builtin.table)}
                  onChange={(v) => updateBuiltin({ match_column: v })}
                  placeholder="column on the master matched against this field" />

                <ConditionsEditor
                  title="Extra conditions on the master row"
                  help="e.g. active = Y (allow null), blacklisted = N (allow null)."
                  conditions={cond} onChange={(next) => updateBuiltin({ conditions: next })}
                  columns={colsOf(builtin.table)}
                  formColumnNames={formColumnNames} headerColumnNames={headerColumnNames} />
              </>
            )}

            {builtin.kind === 'must_be_unique' && (
              <>
                <label style={labelStyle}>Table to check (defaults to this form's table)</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={builtin.table || ''}
                  onChange={(e) => { updateBuiltin({ table: e.target.value || undefined }); loadColumns(e.target.value) }}>
                  <option value="">— this form's table —</option>
                  {tables.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
                </select>
                <label style={labelStyle}>Column that must be unique (defaults to this field)</label>
                <ColumnInput value={builtin.match_column || ''}
                  columns={builtin.table ? colsOf(builtin.table) : formColumnNames}
                  onChange={(v) => updateBuiltin({ match_column: v })}
                  placeholder="defaults to this field's own column" />

                <ConditionsEditor
                  title="Unique within (optional scope conditions)"
                  help={'Restrict the duplicate search, e.g. company = {form.company} for "code unique per company". With none, the value must be unique across the whole table.'}
                  conditions={cond} onChange={(next) => updateBuiltin({ conditions: next })}
                  columns={builtin.table ? colsOf(builtin.table) : formColumnNames}
                  formColumnNames={formColumnNames} headerColumnNames={headerColumnNames} />
                <p style={helpStyle}>
                  Fails when another record already holds this value. When editing,
                  the record being edited is excluded automatically — re-saving a
                  record never flags its own value as a duplicate.
                </p>
              </>
            )}

            {builtin.kind === 'month_open' && (
              <>
                <label style={labelStyle}>Month-definition table</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={builtin.month_table || ''}
                  onChange={(e) => { updateBuiltin({ month_table: e.target.value }); loadColumns(e.target.value) }}>
                  <option value="">— select table —</option>
                  {tables.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
                </select>
                <label style={labelStyle}>From-date column</label>
                <ColumnInput value={builtin.from_date_column || ''} columns={colsOf(builtin.month_table)}
                  onChange={(v) => updateBuiltin({ from_date_column: v })} placeholder="period start" />
                <label style={labelStyle}>To-date column</label>
                <ColumnInput value={builtin.to_date_column || ''} columns={colsOf(builtin.month_table)}
                  onChange={(v) => updateBuiltin({ to_date_column: v })} placeholder="period end" />
                <label style={labelStyle}>Status column</label>
                <ColumnInput value={builtin.status_column || ''} columns={colsOf(builtin.month_table)}
                  onChange={(v) => updateBuiltin({ status_column: v })} placeholder="open/closed flag" />
                <label style={labelStyle}>Open when status equals</label>
                <input style={inputStyle} value={builtin.status_open_value ?? 'Y'}
                  onChange={(e) => updateBuiltin({ status_open_value: e.target.value })} placeholder="Y" />
                <label style={labelStyle}>Date to check (defaults to this field's value)</label>
                <ValueRefControl value={builtin.date_value}
                  formColumnNames={formColumnNames} headerColumnNames={headerColumnNames}
                  onChange={(vr) => updateBuiltin({ date_value: vr })} />
              </>
            )}

            {builtin.kind === 'child_form_has_entry' && (
              <>
                <label style={labelStyle}>Child form that must have a row</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={builtin.child_form || ''}
                  onChange={(e) => updateBuiltin({ child_form: e.target.value })}>
                  <option value="">— select form —</option>
                  {formList.map((f) => (
                    <option key={f.form_no} value={f.form_no}>{f.form_no} · {f.title}</option>
                  ))}
                </select>
                <label style={labelStyle}>Minimum rows</label>
                <input type="number" min={1} style={inputStyle} value={builtin.min_rows ?? 1}
                  onChange={(e) => updateBuiltin({ min_rows: Number(e.target.value) || 1 })} />
                <p style={helpStyle}>Add this at the header form level.</p>
              </>
            )}

            {builtin.kind === 'value_in_range' && (
              <>
                <label style={labelStyle}>Minimum (constant, column or expression)</label>
                <ValueRefControl value={builtin.min} allowExpr projectVars={projectVars}
                  formColumnNames={formColumnNames} headerColumnNames={headerColumnNames}
                  onChange={(vr) => updateBuiltin({ min: vr })} />
                <label style={labelStyle}>Maximum (constant, column or expression)</label>
                <ValueRefControl value={builtin.max} allowExpr projectVars={projectVars}
                  formColumnNames={formColumnNames} headerColumnNames={headerColumnNames}
                  onChange={(vr) => updateBuiltin({ max: vr })} />
                <p style={helpStyle}>
                  Leave a side blank for an open-ended range. Expressions support date
                  helpers today(), now(), days_ago(n), months_ago(n), years_ago(n),
                  date_add(d,n,"year")/date_sub (and +/− math), project variables {'{var.NAME}'},
                  plus round/abs/min/max — e.g. a "must be at least 20" age check sets the
                  maximum date of birth to years_ago(20). Date fields compare by calendar date.
                </p>
              </>
            )}

            {builtin.kind === 'sql_range' && (
              <>
                <label style={labelStyle}>SQL (single-value SELECT)</label>
                <textarea style={{ ...inputStyle, minHeight: 70, fontFamily: 'monospace' }}
                  value={builtin.sql || ''}
                  onChange={(e) => updateBuiltin({ sql: e.target.value })}
                  placeholder="SELECT count(*) FROM orders WHERE customer = {form.customer}" />
                <p style={helpStyle}>Reference form/header values with {'{form.COL}'} / {'{header.COL}'}.</p>
                <label style={labelStyle}>Result minimum (constant, column or expression)</label>
                <ValueRefControl value={builtin.range_min} allowExpr projectVars={projectVars}
                  formColumnNames={formColumnNames} headerColumnNames={headerColumnNames}
                  onChange={(vr) => updateBuiltin({ range_min: vr })} />
                <label style={labelStyle}>Result maximum (constant, column or expression)</label>
                <ValueRefControl value={builtin.range_max} allowExpr projectVars={projectVars}
                  formColumnNames={formColumnNames} headerColumnNames={headerColumnNames}
                  onChange={(vr) => updateBuiltin({ range_max: vr })} />
              </>
            )}
          </>
        )}

        {/* ── EXPRESSION ── */}
        {rule.type === 'expression' && (
          <>
            <label style={labelStyle}>Expression (passes when true)</label>
            <textarea style={{ ...inputStyle, minHeight: 70, fontFamily: 'monospace' }}
              value={rule.expression || ''}
              onChange={(e) => update({ expression: e.target.value })}
              placeholder={'e.g. age({form.dob}) >= 18   ·   {form.status} == "ACTIVE" and {form.amount} > 0'} />
            <p style={helpStyle}>
              Passes when true. Reference columns with {'{form.COL}'} / {'{header.COL}'} / {'{var.NAME}'}.
              Operators + - * / % ** , comparisons, and / or / not. String literals allowed
              ({'{form.status}'} == "ACTIVE", case-insensitive). Functions — dates: today, now, date,
              days_ago, months_ago, years_ago/from_now, date_add(d,n,"year"), date_sub, date_diff,
              age(dob); numbers: round, abs, min, max, floor, ceil, sqrt, pow, mod; strings: len,
              lower, upper, trim, contains, startswith, in_list; logic: iif, coalesce, is_empty,
              not_empty. Dates compare by calendar date.
            </p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <InsertFieldSelect onPick={(t) => update({ expression: `${rule.expression || ''}${t}` })} />
              <InsertMenu label="ƒ Function…" options={CALENDAR_FN_OPTIONS}
                onPick={(t) => update({ expression: `${rule.expression || ''}${t}` })} />
              <InsertMenu label="＋ Variable…"
                options={projectVars.map((v) => ({ label: `{var.${v}}`, token: `{var.${v}}` }))}
                onPick={(t) => update({ expression: `${rule.expression || ''}${t}` })} />
            </div>
          </>
        )}

        {/* ── BUSINESS LOGIC ── */}
        {rule.type === 'business_logic' && (
          <BusinessLogicFields
            value={rule.business_logic || { method: '' }}
            onChange={(bl) => update({ business_logic: bl })}
            methodLabel="Function name"
            onOpenCode={onOpenCode ? openCode : undefined}
          />
        )}

        {/* ── CALL API ── */}
        {rule.type === 'rest_api' && (
          <>
            <label style={labelStyle}>Route via IOFlow API</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={rule.rest_api?.ioflow_api || ''}
              onChange={(e) => update({ rest_api: { ...(rule.rest_api || { url: '' }), ioflow_api: e.target.value || undefined } })}>
              <option value="">— direct URL (below) —</option>
              {ioflowApis.map((n) => <option key={n} value={n}>{n}</option>)}
              {rule.rest_api?.ioflow_api && !ioflowApis.includes(rule.rest_api.ioflow_api) && (
                <option value={rule.rest_api.ioflow_api}>{rule.rest_api.ioflow_api} (not in design)</option>
              )}
            </select>
            <label style={labelStyle}>API URL</label>
            <input style={inputStyle} value={rule.rest_api?.url || ''}
              onChange={(e) => update({ rest_api: { ...(rule.rest_api || { url: '' }), url: e.target.value } })}
              placeholder="https://api.example.com/validate" />
            <div style={rowStyle}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Method</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={rule.rest_api?.method || 'POST'}
                  onChange={(e) => update({ rest_api: { ...(rule.rest_api || { url: '' }), method: e.target.value as 'GET' | 'POST' } })}>
                  <option value="POST">POST</option>
                  <option value="GET">GET</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>On error</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={rule.rest_api?.on_error || 'allow'}
                  onChange={(e) => update({ rest_api: { ...(rule.rest_api || { url: '' }), on_error: e.target.value as 'allow' | 'block' } })}>
                  <option value="allow">Allow (fail-soft)</option>
                  <option value="block">Block the save</option>
                </select>
              </div>
            </div>
            <label style={labelStyle}>Auth header name</label>
            <input style={inputStyle} value={rule.rest_api?.auth_header || ''}
              onChange={(e) => update({ rest_api: { ...(rule.rest_api || { url: '' }), auth_header: e.target.value } })}
              placeholder="Authorization" />
            <label style={labelStyle}>Auth token (literal or {'{var.NAME}'})</label>
            <input style={inputStyle} value={rule.rest_api?.auth_token || ''}
              onChange={(e) => update({ rest_api: { ...(rule.rest_api || { url: '' }), auth_token: e.target.value } })}
              placeholder="{var.API_TOKEN}" />

            <div style={sectionTitleStyle}>Values sent to the API</div>
            {params.map((p, i) => (
              <div key={i} style={rowStyle}>
                <input style={{ ...inputStyle, maxWidth: 130 }} value={p.name}
                  onChange={(e) => {
                    const next = [...params]; next[i] = { ...p, name: e.target.value }
                    update({ rest_api: { ...(rule.rest_api || { url: '' }), params: next } })
                  }} placeholder="param name" />
                <ValueRefControl value={{ source: p.source, value: p.value }}
                  formColumnNames={formColumnNames} headerColumnNames={headerColumnNames}
                  onChange={(vr) => {
                    const next = [...params]; next[i] = { name: p.name, source: vr.source, value: vr.value } as RestApiParam
                    update({ rest_api: { ...(rule.rest_api || { url: '' }), params: next } })
                  }} />
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error, #cf222e)' }}
                  onClick={() => update({ rest_api: { ...(rule.rest_api || { url: '' }), params: params.filter((_, j) => j !== i) } })}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button style={addBtnStyle}
              onClick={() => update({ rest_api: { ...(rule.rest_api || { url: '' }), params: [...params, { name: '', source: 'form', value: '' }] } })}>
              <Plus size={12} /> Add value
            </button>
            <p style={helpStyle}>The endpoint should return {'{"valid": true|false, "message"?: "..."}'}.</p>
          </>
        )}

        {/* preview */}
        <div style={{ marginTop: 16, padding: '8px 12px', background: 'var(--color-bg-secondary)', borderRadius: 4, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          {validationSummary(rule)}
        </div>
      </div>
      )}
    </Modal>
  )
}
